/* eslint-disable @typescript-eslint/no-non-null-assertion */
import {
  AbstractFilterOption,
  AndFilterOption,
  filterFonctionMap,
  FilterValue,
  isAndFilterOption,
  isMultipleFilterOption,
  isOrFilterOption,
  isSingleFilterOption,
  MultipleFilterCreation,
  OrFilterOption,
  SingleFilterCreation,
  SingleFilterCustomCreation,
  SingleFilterOption,
} from '@elio/emanager-front/shared/models';

export function createOrFilterOption<T>(option: MultipleFilterCreation<T>): OrFilterOption<T> {
  const filterId = option.filterId ?? option.translationKey;
  return {
    type: 'or',
    translationKey: option.translationKey,
    id: filterId,
    subFilters: option.filters.map((filter) => ({
      ...filter,
    })),
    hasTranslation: option.hasTranslation ?? true,
    superAdminOnly: option.superAdminOnly ?? false,
    isExtendedByDefault: option.isExtendedByDefault ?? false,
  };
}

export function createAndFilterOption<T>(option: MultipleFilterCreation<T>): AndFilterOption<T> {
  const filterId = option.filterId ?? option.translationKey;
  return {
    type: 'and',
    translationKey: option.translationKey,
    id: filterId,
    subFilters: option.filters.map((filter) => ({
      ...filter,
    })),
    hasTranslation: option.hasTranslation ?? true,
    superAdminOnly: option.superAdminOnly ?? false,
    isExtendedByDefault: option.isExtendedByDefault ?? false,
  };
}

export function createSingleFilterOption<T, S extends keyof T = keyof T>(
  option: SingleFilterCreation<T, S>
): SingleFilterOption<T> {
  const filterId = option.filterId ?? option.translationKey;
  return {
    type: 'single',
    translationKey: option.translationKey,
    id: filterId,
    filterValue: {
      id: filterId,
      property: option.property,
      value: option.value,
      filterFunction: filterFonctionMap.get(option.filterType)!,
    },
    isDefault: option.isDefault ?? false,
  };
}

export function createSingleFilterCustomOption<T>(option: SingleFilterCustomCreation): SingleFilterOption<T> {
  const filterId = option.filterId ?? option.translationKey;
  return {
    type: 'single',
    translationKey: option.translationKey,
    id: filterId,
    filterValue: {
      id: filterId,
      filterFunction: option.filterFunction,
    },
    isDefault: option.isDefault,
  };
}

/**
 * Keep elements matching filter.
 */
export const applyFilters = <T, ST extends T = T>(
  elements: ST[],
  filter: AbstractFilterOption<T>,
  activeFilterIds: string[]
): ST[] => {
  return elements.filter((element) => checkElement(element, filter, activeFilterIds));
};

const checkElement = <T, ST extends T = T>(
  element: ST,
  filter: AbstractFilterOption<T>,
  activeFilterIds: string[]
): boolean => {
  if (isSingleFilterOption(filter)) {
    const filterValue = filter.filterValue;
    return filterValue.filterFunction(
      filterValue.property ? element[filterValue.property] : element,
      filterValue.value
    );
  }
  if (isAndFilterOption(filter)) {
    const activeSubFilters = filter.subFilters.filter((subFilter) => isActive(subFilter, activeFilterIds));
    return (
      activeSubFilters.length === 0 ||
      activeSubFilters.every((subFilter) => checkElement(element, subFilter, activeFilterIds))
    );
  }
  if (isOrFilterOption(filter)) {
    const activeSubFilters = filter.subFilters.filter((subFilter) => isActive(subFilter, activeFilterIds));
    return (
      activeSubFilters.length === 0 ||
      activeSubFilters.some((subFilter) => checkElement(element, subFilter, activeFilterIds))
    );
  }
  throw new Error('Unknown filter type ' + filter.type);
};

/**
 * Check whether filter or one of its descendants is active.
 */
const isActive = <T>(filter: AbstractFilterOption<T>, activeFilterIds: string[]): boolean => {
  if (isSingleFilterOption(filter)) {
    return activeFilterIds.includes(filter.filterValue.id);
  }
  if (isMultipleFilterOption(filter)) {
    return filter.subFilters.some((subFilter) => isActive(subFilter, activeFilterIds));
  }
  throw new Error('Unknown filter type ' + filter.type);
};

/**
 * Search inside filter tree the first FilterValue that pass {@param acceptFn}.
 */
export const findFilterValue = <T>(
  filter: AbstractFilterOption<T>,
  acceptFn: (fv: FilterValue<T>) => boolean
): FilterValue<T> | undefined => {
  let result: FilterValue<T> | undefined = undefined;
  if (isSingleFilterOption(filter)) {
    if (acceptFn(filter.filterValue)) {
      result = filter.filterValue;
    }
  } else if (isMultipleFilterOption(filter)) {
    result = filter.subFilters.reduce((result: FilterValue<T> | undefined, currentValue: AbstractFilterOption<T>) => {
      if (result !== undefined) {
        return result;
      }
      return findFilterValue(currentValue, acceptFn);
    }, undefined);
  }
  return result;
};
